import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';
import { getPostingDetailsQueryKey } from './useGetPostingDetails';

/**
 * React Query mutation hook for updating a job posting
 * Automatically invalidates the posting details query after successful update
 *
 * @param {Object} [options] - Hook options
 * @param {(data: void, variables: import('../types/UpdateJobPosting.parameters').IUpdateJobPostingPayload) => void} [options.onSuccess] - Success callback, receives variables
 * @param {(error: Error) => void} [options.onError] - Error callback
 *
 * @returns {{
 *   mutate: (payload: import('../types/UpdateJobPosting.parameters').IUpdateJobPostingPayload) => void,
 *   mutateAsync: (payload: import('../types/UpdateJobPosting.parameters').IUpdateJobPostingPayload) => Promise<void>,
 *   error: Error | null,
 *   isLoading: boolean,
 *   isError: boolean,
 *   isSuccess: boolean,
 *   reset: () => void
 * }} Mutation object
 *
 * @example
 * // Updating a job posting
 * const { mutate, isLoading } = useUpdateJobPosting({
 *   onSuccess: () => {
 *     console.log('Job posting updated successfully');
 *   }
 * });
 *
 * mutate({
 *   postingId: 'posting-123',
 *   title: 'Senior Dental Hygienist',
 *   professionType: 'RDH',
 *   subProfession: 'General',
 *   officeSpecialty: 'Pediatric',
 *   payType: 'Salary',
 *   minRate: 50000,
 *   maxRate: 75000,
 *   benefits: ['Health Insurance', '401k'],
 *   benefitsOther: 'Gym membership',
 *   scheduleType: 'Full Time',
 *   expectedDays: [1, 2, 3, 4, 5],
 *   description: 'Looking for an experienced dental hygienist',
 *   officeSelected: true,
 *   status: 'active',
 *   recruiterId: 'recruiter-123',
 *   recruiterEmail: 'recruiter@example.com'
 * });
 */
export const useUpdateJobPosting = (options = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, onError } = options;

  const mutation = useMutation({
    mutationFn: PermanentJobsService.updateJobPosting,
    onSuccess: (data, variables) => {
      // Invalidate the posting details query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: getPostingDetailsQueryKey({ postingId: variables.postingId }),
      });

      if (onSuccess) {
        onSuccess(data, variables);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return mutation;
};
